"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Car } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Customer, Vehicle, Appointment, Staff } from "@/types";

interface CustomerDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
  vehicles: Vehicle[];
  appointments: Appointment[];
  staff: Staff[];
}

export function CustomerDetailDialog({
  open,
  onOpenChange,
  customer,
  vehicles,
  appointments,
  staff,
}: CustomerDetailDialogProps) {
  const navigate = useNavigate();
  
  if (!customer) return null;

  const customerAppointments = appointments.filter(
    (apt) => apt.customerId === customer.id
  );
  const customerVehicles = vehicles.filter((v) => v.customerId === customer.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{customer.name}</DialogTitle>
          <DialogDescription>Customer details and history</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p>{customer.phone}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p>{customer.email || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Address</Label>
                  <p>{customer.address || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">City</Label>
                  <p>{customer.city || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Customer Type</Label>
                  <Badge variant="outline" className="capitalize">
                    {customer.customerType.replace(/-/g, " ")}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Category</Label>
                  <Badge variant="outline" className="capitalize">
                    {customer.customerCategory.replace(/-/g, " ")}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge
                    variant={
                      customer.customerStatus === "active"
                        ? "default"
                        : "secondary"
                    }
                    className="capitalize"
                  >
                    {customer.customerStatus}
                  </Badge>
                </div>
                {customer.serviceType && (
                  <div>
                    <Label className="text-muted-foreground">
                      Service Type
                    </Label>
                    <p className="capitalize">
                      {customer.serviceType.replace(/-/g, " ")}
                    </p>
                  </div>
                )}
                {customer.cleaningDay && (
                  <div>
                    <Label className="text-muted-foreground">
                      Cleaning Day
                    </Label>
                    <p>{customer.cleaningDay}</p>
                  </div>
                )}
                {customer.cleaningTime && (
                  <div>
                    <Label className="text-muted-foreground">
                      Cleaning Time
                    </Label>
                    <p>{customer.cleaningTime}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">
                    Interior Cleanings
                  </Label>
                  <p>{customer.numberOfInteriorCleaning}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    Service Providers
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {customer.serviceProviders.map((spId) => {
                      const sp = staff.find((s) => s.id === spId);
                      return sp ? (
                        <Badge key={spId} variant="outline" className="text-xs">
                          {sp.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
              {customer.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm">{customer.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistics */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {formatCurrency(customer.totalSpending)}
                </div>
                <p className="text-xs text-muted-foreground">Total Spending</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {customerVehicles.length}
                </div>
                <p className="text-xs text-muted-foreground">Vehicles</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {customerAppointments.length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Total Appointments
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Vehicles */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Vehicles</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/vehicles")}
              >
                <Car className="mr-2 h-4 w-4" />
                Add Vehicle
              </Button>
            </CardHeader>
            <CardContent>
              {customerVehicles.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No vehicles registered
                </p>
              ) : (
                <div className="space-y-2">
                  {customerVehicles.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div>
                        <p className="font-medium">
                          {vehicle.make} {vehicle.model}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {vehicle.plateNumber} • {vehicle.year} •{" "}
                          {vehicle.color}
                        </p>
                      </div>
                      <Badge variant="outline">
                        {vehicle.totalServices} services
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Appointment History */}
          <Card>
            <CardHeader>
              <CardTitle>Appointment History</CardTitle>
            </CardHeader>
            <CardContent>
              {customerAppointments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No appointment history
                </p>
              ) : (
                <div className="space-y-2">
                  {customerAppointments.slice(0, 5).map((appointment) => (
                    <div
                      key={appointment.id}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div>
                        <p className="font-medium">{appointment.type.replace(/_/g, " ")}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(appointment.date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">
                          {appointment.amount !== null ? formatCurrency(appointment.amount) : "N/A"}
                        </p>
                        <Badge variant="outline">{appointment.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
